import { isFunction } from 'lodash';
import type { GenericList } from '@sda/helpers/helpers.types';
import type { FetchDataType, FirestoreClientData } from '@sda/sdk/interfaces';
import type FirebaseFirestore from 'firebase/firestore';

/**
 * SDK Class to interact with Firestore Transactions
 */
export class Transaction {
  protected collectionReference: FirebaseFirestore.CollectionReference;

  protected transaction: FirebaseFirestore.Transaction;

  protected methods: FirestoreClientData['methods'];

  constructor(
    collectionReference: FirebaseFirestore.CollectionReference,
    transaction: FirebaseFirestore.Transaction,
    methods: FirestoreClientData['methods'],
  ) {
    this.transaction = transaction;
    this.collectionReference = collectionReference;
    this.methods = methods;
  }

  /**
   * Fetches a document reference by id (optional)
   * @param  {string} id
   * @return {FirebaseFirestore.DocumentReference}
   */
  protected getReference(id?: string): FirebaseFirestore.DocumentReference {
    if (id) {
      return this.methods.doc(this.collectionReference, `${id}`);
    }

    return this.methods.doc(this.collectionReference);
  }

  async get<T extends FirebaseFirestore.DocumentData>(docId: string): Promise<T> {
    const docRef = this.getReference(docId);
    const document = await this.transaction.get(docRef) as FirebaseFirestore.DocumentSnapshot<T>;

    let exists = false;

    if (isFunction(document.exists)) {
      exists = document.exists();
    } else {
      // node SDK returns a boolean here instead of a function
      exists = document.exists as any;
    }

    if (exists) {
      return {
        id: document.id,
        ...document.data(),
      };
    }

    return null;
  }

  /**
   * Adds data to a collection
   * @return {FirebaseFirestore.DocumentReference}
   */
  add(docData: FirebaseFirestore.DocumentData, docId?: string): FirebaseFirestore.DocumentReference {
    const docRef = this.getReference(docId);

    this.transaction.set(docRef, docData);

    return docRef;
  }

  /**
   * Sets data to a document in a collection
   * @return {FirebaseFirestore.Transaction}
   */
  set(docId: string, docData: FirebaseFirestore.DocumentData): FirebaseFirestore.Transaction {
    const docRef = this.getReference(docId);

    return this.transaction.set(docRef, docData);
  }

  /**
   * Updates data to a document in a collection
   * @return {FirebaseFirestore.Transaction}
   */
  update(docId: string, docData: FirebaseFirestore.DocumentData): FirebaseFirestore.Transaction {
    const docRef = this.getReference(docId);

    return this.transaction.update(docRef, docData);
  }

  /**
   * Deletes a document in a collection
   * @param  {string} docId
   * @return {FirebaseFirestore.Transaction}
   */
  delete(docId: string): FirebaseFirestore.Transaction {
    const docRef = this.getReference(docId);

    return this.transaction.delete(docRef);
  }

  /**
   * Alias for transaction.delete
   * @param  {string} docId
   */
  remove(docId: string): FirebaseFirestore.Transaction {
    return this.delete(docId);
  }

  list<T = any>(...args: any): Promise<GenericList<T & FetchDataType>> {
    console.log(args);

    throw new Error('This should not be used');
  }

  listBy<T = any>(...args: any): Promise<GenericList<T & FetchDataType>> {
    console.log(args);

    throw new Error('This should not be used');
  }
}
